import { ActionReference, DefaultGameState, GameObjectReference, GameState } from "@shared/types";
import { css, html, htmlArray } from "../helpers/webComponents";
import { GameEventService } from "../services/GameEventService";
import { GameRouteService } from "../services/GameRouteService";
import { Page } from "../enums/Page";
import { StopwatchComponent } from "./StopwatchComponent";

/** CSS affecting the {@link CanvasComponent} */
const styles: string = css`
    :host {
        font-family: "Orbitron", sans-serif;
        width: 100%;
        max-width: 1400px;
        height: 100%;
        display: grid;
        grid-template-columns: 1fr;
        grid-template-rows: auto 65vh 20vh;
        grid-gap: 0;
        background: radial-gradient(circle at center, #0a0f23 20%, #010310 100%);
        color: #cceeff;
        text-shadow: 0 0 4px #00ffff, 0 0 8px #00ffff;
    }

    .title-wrapper {
        display: flex;
        justify-content: space-between;
        align-items: center;
        padding: 0 2rem;
        margin-top: 10px;
        flex-wrap: wrap;
    }

    .title {
        font-size: 2rem;
        text-transform: uppercase;
        letter-spacing: 2px;
        flex: 1;
        min-width: 200px;
    }

    game-stopwatch {
        flex-shrink: 0;
        margin-left: auto;
    }

    .header {
        display: flex;
        flex-direction: column;
        align-items: center;
        position: relative;
        height: 100%;
        overflow: hidden;
    }

    .header img {
        width: auto;
        height: 90%;
        image-rendering: pixelated;
    }

    .header img:nth-child(n + 2) {
        position: absolute;
    }

    .content{
        overflow-y: scroll;
    }

    /* Algemene knoppen (Acties) */
    .button {
        background-color: rgba(20, 0, 30, 0.85);
        border: 2px solid #ff00ff;
        padding: 10px 15px;
        margin: 5px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
        font-size: 0.8rem;
        color: #ff66ff;
        text-shadow: 0 0 8px #ff00ff;
        transition: 0.3s;
        border-radius: 10px;
        white-space: nowrap;
    }

    .button:hover {
        background-color: #ff00ff;
        color: white;
        box-shadow: 0 0 15px #ff00ff;
    }

    .button.active {
        background-color: #ff00ff;
        color: white;
        box-shadow: 0 0 20px #ff00ff;
    }

    /* Objecten (selectie knoppen) */
    .button-select {
        background-color: rgba(5, 0, 20, 0.8);
        border: 2px solid #845ec2;
        padding: 10px 15px;
        margin: 5px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
        font-size: 0.8rem;
        color: #d5aaff;
        text-shadow: 0 0 6px #845ec2;
        transition: 0.3s;
        border-radius: 10px;
        white-space: nowrap;
    }

    .button-select:hover {
        background-color: #845ec2;
        color: white;
        box-shadow: 0 0 12px #845ec2;
    }

    .button-select.active {
        background-color: #845ec2;
        color: white;
        box-shadow: 0 0 20px #845ec2;
    }

    /* 🛫 Reisknoppen */
    .button-travel {
        background-color: rgba(0, 10, 20, 0.8);
        border: 2px solid #0d29ced9;
        padding: 10px 15px;
        margin: 5px;
        text-transform: uppercase;
        cursor: pointer;
        display: inline-block;
        user-select: none;
        font-size: 0.8rem;
        color: #0d29ced9;
        text-shadow: 0 0 6px #0d29ced9;
        transition: 0.3s;
        border-radius: 10px;
        white-space: nowrap;
    }

    .button-travel:hover {
        background-color: #0d29ced9;
        color: white;
        box-shadow: 0 0 15px #0d29ced9;
    }

    .button-travel.active {
        background-color: #0d29ced9;
        color: white;
        box-shadow: 0 0 20px #0d29ced9;
    }

    /* Knoppenbalk */
    .footer {
        border-radius: 10px 10px 0 0;
        background: linear-gradient(130deg, #1a0845, #5c11d3, #1a0845, #5c11d3, #1a0845) ;
        border: 1px solid #00eaff;
        display: flex;
        min-height: 60px;
        height: auto;
        justify-content: center;
        align-items: center;
        overflow: hidden;
    }

    .footer .buttons {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        max-width: 100%;
        overflow-y: auto;
        gap: 5px;
        align-items: center;    
        white-space: nowrap; /* extra zekerheid */

    }

    .footer .buttons::-webkit-scrollbar {
    height: 2px; /* ← veel smaller dan standaard */
    margin-top: 10px;
}

.footer .buttons::-webkit-scrollbar-thumb {
    background: #ffffff;
    border-radius: 2px;
}

.footer .buttons::-webkit-scrollbar-track {
    background: transparent;
}

    /* Mobiele responsiviteit */
    @media (max-width: 768px) {
        .title-wrapper {
            flex-direction: column;
            align-items: center;
            padding: 0 1rem;
        }

        .title {
            font-size: 1.5rem;
            text-align: center;
        }

        game-stopwatch {
            align-self: center;
        }

        .button, .button-select, .button-travel {
            font-size: 0.7rem;
            padding: 8px 12px;
            margin: 4px;
        }

        .footer .buttons {
            flex-direction: column;
            align-items: center;
        }

        @media (max-width: 480px) {
        .title {
            font-size: 1.2rem;
        }

        .button, .button-select, .button-travel {
            font-size: 0.6rem;
            padding: 6px 10px;
            margin: 3px;
        }

        .footer .buttons {
            flex-direction: column;
            gap: 3px;
        }

        .header img {
            height: 70%;
        }
    }
`;

export class CanvasComponent extends HTMLElement {
    /** Instance of the game event service */
    private readonly _gameEventService: GameEventService = new GameEventService();
    /** Instance of the game route service */
    private readonly _gameRouteService: GameRouteService = new GameRouteService();

    /** Current game state */
    private _currentGameState?: DefaultGameState;
    /** Current active action button */
    private _selectedActionButton?: ActionReference;
    /** Current active game object buttons */
    private _selectedGameObjectButtons: Set<GameObjectReference> = new Set<GameObjectReference>();
    private _stopwatchComponent!: StopwatchComponent;
    private _playedSounds: Set<string> = new Set();

    /**
     * The "constructor" of a Web Component
     */
    public connectedCallback(): void {
        this.attachShadow({ mode: "open" });
        this._stopwatchComponent = document.createElement("game-stopwatch") as StopwatchComponent;

        void this.refreshGameState();

        window.addEventListener("game:update-state", (event: Event) => {
            const state: GameState = (event as CustomEvent<GameState>).detail;
            this.updateGameState(state);
        });
    }

    /**
     * Refresh the current game state
     */
    private async refreshGameState(): Promise<void> {
        const state: GameState = await this._gameRouteService.getGameState();

        this.updateGameState(state);
    }

    /**
     * Update the canvas to the provided game state
     *
     * @param state Game state to update the canvas to
     */
    private updateGameState(state: GameState): void {
        // Handle switching pages, if requested.
        if (state.type === "switch-page") {
            this._gameEventService.switchPage(state.page as Page);

            return;
        }

        // Reset the component
        this._currentGameState = state;

        this._selectedActionButton = undefined;
        this._selectedGameObjectButtons.clear();

        // Refresh the web component
        this.render();
        void this._stopwatchComponent.setFromBackend();
        this.startStopwatch();
    }

    private startStopwatch(): void {
        if (!this._stopwatchComponent.isRunning() && !this._stopwatchComponent.isStopped()) {
            void this._stopwatchComponent.resumeAfterRender();
        }
    }

    public async startStopwatchInDOM(): Promise<void> {
        if (typeof this._stopwatchComponent.startTimerFromBackend === "function") {
            await this._stopwatchComponent.startTimerFromBackend();
        }
    }

    /**
     * Render the contents of this page
     */
    private render(): void {
        if (!this.shadowRoot) {
            return;
        }

        const elements: HTMLElement[] = htmlArray`
            <style>
                ${styles}
            </style>

            ${this.renderHeader()}
            ${this.renderContent()}
            ${this.renderFooter()}
        `;

        while (this.shadowRoot.firstChild) {
            this.shadowRoot.firstChild.remove();
        }

        const titleWrapper: HTMLDivElement = document.createElement("div");
        titleWrapper.className = "title-wrapper";

        const titleDiv: HTMLDivElement = document.createElement("div");
        titleDiv.className = "title";
        titleDiv.textContent = this._currentGameState?.roomName ?? "";

        titleWrapper.appendChild(titleDiv);
        titleWrapper.appendChild(this._stopwatchComponent);

        this.shadowRoot.appendChild(titleWrapper);
        this.shadowRoot.append(...elements);
    }

    /**
     * Render the header element
     *
     * @returns String with raw HTML for the header element. Can be empty.
     */
    private renderHeader(): string {
        let headerContent: string = "";

        if (this._currentGameState?.roomImages.includes("worldmap")) {
            headerContent = `
                <world-map style="height: 85%; width: auto;"></world-map>
            `;
        }
        else {
            const roomImages: string[] | undefined = this._currentGameState?.roomImages;

            if (roomImages && roomImages.length > 0) {
                headerContent = roomImages.map(url => `<img src="/assets/img/rooms/${url}.png" />`).join("");
            }
        }
        return `
            <div class="header">
                ${headerContent}
                <game-inventory></game-inventory>
            </div>
        `;
    }

    /**
     * Render the content element
     *
     * @returns String with raw HTML for the content element
     */
    private renderContent(): string {
        const lines: string[] = this._currentGameState?.text || [];

        const renderedLines: string[] = lines.map(originalText => {
            let text: string = originalText;

            if (text.includes("*play-sound:casino*")) {
                if (!this._playedSounds.has("casino")) {
                    const audio: HTMLAudioElement = new Audio("assets/sounds/casino.mp3");
                    void audio.play();
                    this._playedSounds.add("casino");
                }

                // Verwijder de trigger, houd de rest
                text = text.replace("*play-sound:casino*", "").trim();
            }

            return text ? `<p>${text}</p>` : "";
        });

        return `<div class="content">
                    ${renderedLines.join("")}
                </div>`;
    }

    /**
     * Render the footer element
     *
     * @returns HTML element of the footer
     */
    private renderFooter(): HTMLElement {
        return html`<div class="footer">
                <div class="buttons">
                    <div>
                        ${this._currentGameState?.actions.map(button => this.renderActionButton(button))}
                    </div>
                    <div class= "buttons-select">
                        ${this._selectedActionButton
                            ? this._currentGameState?.objects.map(button => this.renderGameObjectButton(button)) || ""
                            : ""
                        }
                    </div>
                </div>
            </div>`
        ;
    }

    /**
     * Render an action button for a given action reference
     *
     * @returns HTML element of the action button
     */
    private renderActionButton(button: ActionReference): HTMLElement {
        const isActive: boolean = this._selectedActionButton === button;

        const isTravel: boolean = button.alias.startsWith("go-"); // fallback voor veiligheid

        const className: string = isTravel
            ? `button-travel ${isActive ? "active" : ""}` // paars
            : `button ${isActive ? "active" : ""}`; // roze

        const element: HTMLElement = html`<a class=${className}>
                ${button.name}
            </a>`
        ;

        element.addEventListener("click", () => this.handleClickAction(button));

        return element;
    }

    /**
     * Render a game object button for a given game object reference
     *
     * @returns HTML element of the game object button
     */
    private renderGameObjectButton(button: GameObjectReference): HTMLElement {
        const isSelected: boolean = this._selectedGameObjectButtons.has(button);
        const className: "button-select active" | "button-select" = isSelected ? "button-select active" : "button-select";

        const element: HTMLElement = html`<a class="${className}">
            ${button.name}
        </a>`;

        element.addEventListener("click", () => this.handleClickGameObject(button));

        return element;
    }

    /**
     * Handle the click on an action button
     *
     * @param button Action button that was clicked
     */
    private async handleClickAction(button: ActionReference): Promise<void> {
        // If this actions needs a game object, show the available game objects.
        if (button.needsObject) {
            this._selectedActionButton = button;
            this._selectedGameObjectButtons.clear();

            this.render();

            return;
        }

        // Otherwise, execute the action and update the game state.
        const state: GameState | undefined = await this._gameRouteService.executeAction(button.alias);

        if (state === undefined) {
            return;
        }

        this.updateGameState(state);
        await this._stopwatchComponent.setFromBackend(); // ← hier ophalen of de stopwatch gestopt moet zijn
        this._stopwatchComponent.updateDisplay(); // <- zorg dat eindtijd getoond wordt

        if (button.alias === "enter-username") {
            this.render(); // eerst DOM renderen
            void this.startStopwatchInDOM(); // dan stopwatch starten
        }
    }

    /**
     * Handle the click on a game object button
     *
     * @param button Game object button that was clicked
     */
    private async handleClickGameObject(button: GameObjectReference): Promise<void> {
        // If no action button was clicked, do not try to handle this click.
        if (!this._selectedActionButton) {
            return;
        }

        // Add the game object to list of selected game objects
        this._selectedGameObjectButtons.add(button);

        // Try to execute the action with all game objects on the list
        const state: GameState | undefined = await this._gameRouteService.executeAction(
            this._selectedActionButton.alias,
            [...this._selectedGameObjectButtons].map(e => e.alias)
        );

        // If 2 more game objects where on the list, clear it.
        if (this._selectedGameObjectButtons.size >= 2) {
            this._selectedActionButton = undefined;
            this._selectedGameObjectButtons.clear();
        }

        // Refresh the web component
        this.render();

        // If no state was returned, exit silently. This can happen when an action needs more than 1 game object.
        if (state === undefined) {
            return;
        }

        // Otherwise, update the game state.
        this.updateGameState(state);
    }
};
